import { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useLanguage } from '@/contexts/LanguageContext';

type BilingualField = string | { ar: string; en: string };

interface Partner {
  id: string;
  name: BilingualField;
  logo: string;
  order: number;
}

export function LandingPartners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const { t, language } = useLanguage();

  const localize = (field: BilingualField): string => {
    if (typeof field === 'string') return field;
    return field[language] || field.ar || field.en || '';
  };

  // Create infinite loop by triplicating the array (reversed order)
  const infinitePartners = partners.length > 0 
    ? [...partners.slice().reverse(), ...partners.slice().reverse(), ...partners.slice().reverse()] 
    : [];

  useEffect(() => {
    fetchPartners();
  }, []);

  // Initialize scroll to middle set after partners load
  useEffect(() => {
    if (partners.length > 0 && containerRef.current) {
      const container = containerRef.current;
      // Wait for layout to complete
      setTimeout(() => {
        const cardElement = container.children[0] as HTMLElement;
        if (cardElement) {
          // Scroll to the start of the middle set (second copy)
          const middlePosition = cardElement.offsetWidth * partners.length;
          container.scrollLeft = middlePosition;
        }
      }, 100);
    }
  }, [partners.length]);

  // Monitor scroll position and reset if needed for infinite effect
  useEffect(() => {
    const container = containerRef.current;
    if (!container || partners.length === 0) return;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      const cardElement = container.children[0] as HTMLElement;
      if (!cardElement) return;

      const cardWidth = cardElement.offsetWidth;
      const scrollLeft = container.scrollLeft;
      const totalOriginalWidth = cardWidth * partners.length;

      // If scrolled past the end of second set, jump to start of second set
      if (scrollLeft >= totalOriginalWidth * 2) {
        isScrollingRef.current = true;
        container.scrollLeft = totalOriginalWidth;
        setTimeout(() => { isScrollingRef.current = false; }, 50);
      }
      // If scrolled before the start of second set, jump to end of second set
      else if (scrollLeft < totalOriginalWidth) {
        isScrollingRef.current = true;
        container.scrollLeft = totalOriginalWidth * 2 - cardWidth;
        setTimeout(() => { isScrollingRef.current = false; }, 50);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [partners.length]);

  // Auto-scroll effect
  useEffect(() => {
    if (partners.length === 0) return;

    const interval = setInterval(() => {
      if (!isHovering && containerRef.current && !isScrollingRef.current) {
        const container = containerRef.current;
        const cardElement = container.children[0] as HTMLElement;
        if (!cardElement) return;
        
        const scrollAmount = cardElement.offsetWidth;
        
        // Scroll left (decrease scrollLeft) - the infinite scroll handler will take care of wrapping
        container.scrollTo({ 
          left: container.scrollLeft - scrollAmount, 
          behavior: 'smooth' 
        });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isHovering, partners.length]);

  const fetchPartners = async () => {
    try {
      const partnersQuery = query(
        collection(db, 'landing_partners'),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(partnersQuery);

      const partnersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Partner[];

      setPartners(partnersData);
    } catch (err) {
      console.error('Error fetching partners:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (direction: 'prev' | 'next') => {
    const container = containerRef.current;
    if (!container) return;

    const cardElement = container.children[0] as HTMLElement;
    if (!cardElement) return;
    
    const scrollAmount = cardElement.offsetWidth;
    
    if (direction === 'next') {
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    } else {
      container.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">{t('landing.common.loading')}</div>
        </div>
      </section>
    );
  }

  return (
    <section id="partners" className="py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-[#004250] mb-12">{t('landing.partners.title')}</h2>
        
        <div 
          className="relative group"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div 
            ref={containerRef}
            dir="ltr"
            className="flex overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {infinitePartners.map((partner, index) => {
              const name = localize(partner.name);
              
              return (
              <div
                key={`${partner.id}-${index}`}
                className="min-w-full md:min-w-[25%] px-4 py-8"
              >
                <div className="bg-gray-50 p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300">
                  <img src={partner.logo} alt={name} className="w-full h-32 object-contain mb-4" />
                  <h3 className="text-center font-semibold text-[#004250]">{name}</h3>
                </div>
              </div>
            )}
            )}
          </div>

          <button
            onClick={() => handleNavigation('next')}
            className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-2 bg-white/90 hover:bg-white text-[#004250] p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <button
            onClick={() => handleNavigation('prev')}
            className="absolute left-0 top-1/2 -translate-y-1/2 translate-x-2 bg-white/90 hover:bg-white text-[#004250] p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        </div>
      </div>
    </section>
  );
}

